// src/components/shared/SponsorStrip.js
// Displays all town sponsors by tier: Founding Partner, Gold, Community Supporter
// Promoted events shown inline in feed

import React, { useState, useEffect } from 'react';
import { db } from '../../lib/firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { incrementSponsorImpression, incrementSponsorClick } from '../../lib/db';

// Tier config
const TIER_CONFIG = {
  founding: {
    label: 'Proudly supported by',
    style: {
      background: 'linear-gradient(135deg, #2C342C, #4A544A)',
      color: 'white',
      borderRadius: 16,
      padding: '12px 16px',
      marginBottom: 10,
    },
    labelStyle: { color: 'rgba(255,255,255,0.6)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 },
    nameStyle: { color: 'white', fontSize: 14, fontWeight: 800 },
    taglineStyle: { color: 'rgba(255,255,255,0.7)', fontSize: 11 },
    logoStyle: { background: 'rgba(255,255,255,0.15)', borderRadius: 10 },
  },
  gold: {
    label: 'Community Partner',
    style: {
      background: '#FDF3E0',
      border: '1.5px solid #E8C870',
      borderRadius: 14,
      padding: '10px 14px',
      marginBottom: 8,
    },
    labelStyle: { color: '#B07828', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 },
    nameStyle: { color: '#7A5010', fontSize: 13, fontWeight: 800 },
    taglineStyle: { color: '#9A7030', fontSize: 11 },
    logoStyle: { background: '#F5E0B0', borderRadius: 9 },
  },
  supporter: {
    label: 'Community Supporter',
    style: {
      background: 'var(--sage-pale)',
      border: '1px solid var(--sage-light)',
      borderRadius: 12,
      padding: '8px 12px',
      marginBottom: 6,
    },
    labelStyle: { color: 'var(--muted)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 },
    nameStyle: { color: 'var(--sage-dark)', fontSize: 12, fontWeight: 700 },
    taglineStyle: { color: 'var(--muted)', fontSize: 11 },
    logoStyle: { background: 'var(--sage-light)', borderRadius: 8 },
  },
};

// Demo sponsors — replaced by Firestore data
const DEMO_SPONSORS = [];

export const SponsorStrip = ({ townId, page }) => {
  const [sponsors, setSponsors] = useState(DEMO_SPONSORS);

  useEffect(() => {
    if (!townId) return;
    const load = async () => {
      try {
        const q = query(
          collection(db, 'sponsors'),
          where('townId', '==', townId),
          where('active', '==', true),
          orderBy('tierOrder')
        );
        const snap = await getDocs(q);
        if (snap.docs.length > 0) {
          const loaded = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            // No pages field = this sponsor predates page-targeting, treat as "everywhere"
            .filter(s => !page || !s.pages || s.pages.includes(page));
          setSponsors(loaded);
          loaded.forEach(s => incrementSponsorImpression(s.id));
        }
      } catch { /* use demo data */ }
    };
    load();
  }, [townId, page]);

  if (sponsors.length === 0) return null;

  const founding = sponsors.filter(s => s.tier === 'founding');
  const gold = sponsors.filter(s => s.tier === 'gold');
  const supporters = sponsors.filter(s => s.tier === 'supporter');

  return (
    <div style={{ marginBottom: 4 }} role="region" aria-label="Town sponsors">
      {/* Founding partner — full width, premium display */}
      {founding.map(s => (
        <SponsorCard key={s.id} sponsor={s} config={TIER_CONFIG.founding} />
      ))}

      {/* Gold sponsors — side by side if two */}
      {gold.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: gold.length > 1 ? '1fr 1fr' : '1fr',
          gap: 8, marginBottom: 8,
        }}>
          {gold.map(s => (
            <SponsorCard key={s.id} sponsor={s} config={TIER_CONFIG.gold} compact />
          ))}
        </div>
      )}

      {/* Community supporters — horizontal scroll roll */}
      {supporters.length > 0 && (
        <div style={supporterRollStyle}>
          <span style={supporterLabelStyle}>Community supporters:</span>
          {supporters.map((s, i) => (
            <span key={s.id} style={supporterNameStyle}>
              {s.logoUrl
                ? <img src={s.logoUrl} alt="" style={{ width: 14, height: 14, objectFit: 'contain', borderRadius: 3, verticalAlign: 'middle', marginRight: 3 }} />
                : `${s.emoji} `}
              {s.name}{i < supporters.length - 1 ? ' ·' : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const SponsorCard = ({ sponsor, config, compact = false }) => {
  const content = (
    <div style={{ ...config.style, display: 'flex', alignItems: 'center', gap: 10, cursor: sponsor.website ? 'pointer' : 'default' }}>
      <div style={{ ...config.logoStyle, width: compact ? 32 : 40, height: compact ? 32 : 40, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: compact ? 18 : 22, flexShrink: 0, overflow: 'hidden' }}>
        {sponsor.logoUrl ? <img src={sponsor.logoUrl} alt={sponsor.name} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 'inherit' }} /> : (sponsor.emoji || '🏢')}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={config.labelStyle}>{config.label}</div>
        <div style={{ ...config.nameStyle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {sponsor.name}
        </div>
        {!compact && sponsor.tagline && (
          <div style={config.taglineStyle}>{sponsor.tagline}</div>
        )}
      </div>
    </div>
  );

  if (sponsor.website) {
    return (
      <a href={sponsor.website} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'block' }}
        onClick={() => incrementSponsorClick(sponsor.id)}
        aria-label={`Sponsor: ${sponsor.name}`}>
        {content}
      </a>
    );
  }
  return <div aria-label={`Sponsor: ${sponsor.name}`}>{content}</div>;
};

const supporterRollStyle = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center',
  gap: 6, background: 'var(--sage-pale)',
  borderRadius: 10, padding: '8px 12px', marginBottom: 4,
};
const supporterLabelStyle = {
  fontSize: 10, fontWeight: 700, color: 'var(--muted)',
  textTransform: 'uppercase', letterSpacing: 1, marginRight: 2,
};
const supporterNameStyle = {
  fontSize: 11, fontWeight: 600, color: 'var(--sage-dark)',
};

// ── Promoted Event Badge ──────────────────────────────────────
export const PromotedBadge = ({ type = 'promoted' }) => (
  <span className="promoted-badge" aria-label={type === 'featured' ? 'Featured event' : 'Promoted event'}>
    {type === 'featured' ? '⭐ Featured' : '📣 Promoted'}
  </span>
);
